import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { QUOTE_JOURNEY_TYPE } from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import axioInstance from "@/libs/axios";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { PayloadType } from "@/types/ContactDetailsTypes";
import { QuoteData } from "@/types/quote/quote-data";
import { trimFormValue } from "@/utils/form-input.utils";
import logError from "@/utils/logger";

export default function useSaveContactDetails(resetTrigger: () => void) {
  const [data, setData] = useState<null | QuoteData>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | AxiosError>(null);
  const dispatch = useDispatch();
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { attributes, version, referenceId } = quoteResponse;

  const saveContactDetails = useCallback(
    async (formData: PayloadType) => {
      const body = {
        data: {
          type: QUOTE_JOURNEY_TYPE,
          channel: "ONLINE",
          version,
          viewPage: Routes.ContactDetails,
          attributes: {
            domain: attributes.domain,
            organiser: {
              email: trimFormValue(formData.email),
              telephone: trimFormValue(formData.telephone),
            },
            marketingConsent: formData.marketingConsent,
            ...(formData.quoteDataHoldingConsent !== undefined && {
              quoteDataHoldingConsent: formData.quoteDataHoldingConsent,
            }),
          },
        },
      };

      try {
        setLoading(true);
        const response = await axioInstance.patch(
          `${process.env.NEXT_PUBLIC_BASE_URL}/quote/${referenceId}/contactDetails`,
          body
        );
        setData(response.data.data);
        // Update Redux immediately to ensure version is synced even if component unmounts
        // This is critical for interstitial flow where navigation happens before API completes
        dispatch(updateQuote(response.data.data));
      } catch (err) {
        const axiosError = err as AxiosError;
        logError(axiosError, { location: "Contact Page Save" });
        setError(axiosError);
      } finally {
        setLoading(false);
        resetTrigger();
      }
    },
    [attributes.domain, referenceId, resetTrigger, version, dispatch]
  );

  return { data, loading, error, saveContactDetails };
}
